import { createContext, useState, useMemo } from "react";
import { createTheme } from "@mui/material/styles";

// color design tokens export
export const tokens = (mode) => ({
  ...(mode === "dark"
    ? {
      grey: {
        100: "#e0e0e0",
        200: "#c2c2c2",
        300: "#a3a3a3",
        400: "#858585",
        500: "#666666",
        600: "#525252",
        700: "#3d3d3d",
        800: "#292929",
        900: "#141414",
      },
      primary: {
        100: "#d0d1d5",
        200: "#a1a4ab",
        300: "#727681",
        400: "#1F2A40", // Sidebar & table bg (dark navy-blue)
        500: "#141b2d", // Main background (deep navy)
        600: "#101624",
        700: "#0c101b",
        800: "#080b12",
        900: "#040509",
      },
      greenAccent: {
        100: "#dbf5ee",
        200: "#b7ebde",
        300: "#94e2cd",
        400: "#70d8bd",
        500: "#4cceac",
        600: "#3da58a",
        700: "#2e7c67",
        800: "#1e5245",
        900: "#0f2922",
      },
      redAccent: {
        100: "#f8dcdb",
        200: "#f1b9b7",
        300: "#e99592",
        400: "#e2726e",
        500: "#db4f4a",
        600: "#af3f3b",
        700: "#832f2c",
        800: "#58201e",
        900: "#2c100f",
      },
      blueAccent: {
        100: "#e1e2fe",
        200: "#c3c6fd",
        300: "#a4a9fc",
        400: "#868dfb", // Active hover color
        500: "#6870fa", // Active selected color
        600: "#535ac8",
        700: "#3e4396",
        800: "#2a2d64",
        900: "#151632",
      },
    }
    : {
      grey: {
        100: "#15161a",
        200: "#2a2c34",
        300: "#3e434d",
        400: "#535967",
        500: "#686f81",
        600: "#868c9a",
        700: "#a4a9b3",
        800: "#c3c5cd",
        900: "#e1e2e6",
      },
      primary: {
        100: "#EEEFEF", // Light mode background
        200: "#ecf0f3", // Platinum for light mode background
        300: "#e0e4e7",
        400: "#d9e2e8",
        500: "#1f76ad",
        600: "#7b8694",
        700: "#7caccb",
        800: "#3e4d62",
        900: "#1F2A40",
      },
      greenAccent: {
        100: "#072617",
        200: "#0e4c2e",
        300: "#147344",
        400: "#1b995b",
        500: "#22bf72",
        600: "#4ecc8e",
        700: "#7ad9aa",
        800: "#a7e5c7",
        900: "#d3f2e3",
      },
      redAccent: {
        100: "#2a080b",
        200: "#541016",
        300: "#7e1820",
        400: "#a8202b",
        500: "#d22836",
        600: "#db535e",
        700: "#e47e86",
        800: "#eda9af",
        900: "#f6d4d7",
      },
      blueAccent: {
        100: "#0a0c0f",
        200: "#14181e",
        300: "#1e242d",
        400: "#28303c",
        500: "#323c4b",
        600: "#5b636f",
        700: "#848a93",
        800: "#adb1b7",
        900: "#d6d8db",
      },
    }),
});
// mui theme settings
export const themeSettings = (mode) => {
  const colors = tokens(mode);
  return {
    palette: {
      mode: mode,
      ...(mode === "dark"
        ? {
          primary: {
            main: "#4cceac",
          },
          secondary: {
            main: colors.greenAccent[500],
          },
          neutral: {
            dark: colors.grey[700],
            main: colors.grey[500],
            light: colors.grey[100],
          },
          background: {
            default: colors.primary[500], // Deep navy: #141b2d
          },
        }
        : {
          primary: {
            main: "#1766A2", // Standard blue for light mode
          },
          secondary: {
            main: colors.greenAccent[500],
          },
          neutral: {
            dark: colors.grey[700],
            main: colors.grey[500],
            light: colors.grey[100],
          },
          background: {
            default: colors.primary[100], // Off-white: #EEEFEF
          },
        }),
    },

    typography: {
      fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
      fontSize: 12,
      h1: {
        fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
        fontSize: 40,
      },
      h2: {
        fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
        fontSize: 32,
      },
      h3: {
        fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
        fontSize: 24,
      },
      h4: {
        fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
        fontSize: 20,
      },
      h5: {
        fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
        fontSize: 16,
      },
      h6: {
        fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
        fontSize: 14,
      },
    },
  };
};

// context for color mode
export const ColorModeContext = createContext({
  toggleColorMode: () => { },
  setMode: () => { },
});

export const useMode = () => {
  const [mode, setMode] = useState("light"); // Default mode set to light

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () =>
        setMode((prev) => (prev === "light" ? "dark" : "light")),
      setMode: (newMode) => setMode(newMode),
    }),
    []
  );

  const theme = useMemo(() => createTheme(themeSettings(mode)), [mode]);
  return [theme, colorMode];
};

export const DarkModeContext = createContext({
  toggleDarkMode: () => { },
});

export const useDarkMode = () => {
  const [mode, setMode] = useState("dark"); // Default mode set to dark

  const darkMode = useMemo(
    () => ({
      toggleDarkMode: () =>
        setMode((prev) => (prev === "dark" ? "light" : "dark")),
    }),
    []
  );

  const theme = useMemo(() => createTheme(themeSettings(mode)), [mode]);
  return [theme, darkMode];
};
